import {
  DEFAULT_V5_COLOR_MODE,
  DEFAULT_V5_DENSITY,
  V5_COLOR_MODES,
  V5_DENSITY_MODES,
  type V5ColorMode,
  type V5DensityMode,
} from '@routific/hive/v5';
import { Instance, types } from 'mobx-state-tree';

const V5_THEME_ENABLED_STORAGE_KEY = 'internalTools.v5ThemeEnabled';
const V5_COLOR_MODE_STORAGE_KEY = 'internalTools.v5ColorMode';
const V5_DENSITY_STORAGE_KEY = 'internalTools.v5Density';

const InternalToolsUI = types
  .model('InternalToolsUI')
  .props({
    isV5ThemeEnabled: types.optional(types.boolean, false),
    v5ColorMode: types.optional(types.enumeration<V5ColorMode>('V5ColorMode', [...V5_COLOR_MODES]), DEFAULT_V5_COLOR_MODE),
    v5Density: types.optional(types.enumeration<V5DensityMode>('V5DensityMode', [...V5_DENSITY_MODES]), DEFAULT_V5_DENSITY),
    showV5PlaygroundModal: types.optional(types.boolean, false),
    // Hidden, session-only dark-mode override (toggled via Shift+D). Deliberately
    // omitted from getInitialInternalToolsUIState and never written to storage, so
    // it always starts light and resets on reload.
    sessionDarkMode: types.optional(types.boolean, false),
  })
  .views((self) => ({
    get effectiveColorMode(): V5ColorMode {
      return self.isV5ThemeEnabled ? self.v5ColorMode : DEFAULT_V5_COLOR_MODE;
    },
    get effectiveDensity(): V5DensityMode {
      return self.isV5ThemeEnabled ? self.v5Density : DEFAULT_V5_DENSITY;
    },
  }))
  .actions((self) => ({
    setIsV5ThemeEnabled(value: boolean) {
      self.isV5ThemeEnabled = value;
      localStorage.setItem(V5_THEME_ENABLED_STORAGE_KEY, JSON.stringify(value));
    },
    setV5ColorMode(value: V5ColorMode) {
      self.v5ColorMode = value;
      localStorage.setItem(V5_COLOR_MODE_STORAGE_KEY, value);
    },
    setV5Density(value: V5DensityMode) {
      self.v5Density = value;
      localStorage.setItem(V5_DENSITY_STORAGE_KEY, value);
    },
    setShowV5PlaygroundModal(value: boolean) {
      self.showV5PlaygroundModal = value;
    },
    toggleSessionDarkMode() {
      self.sessionDarkMode = !self.sessionDarkMode;
    },
  }));

const parseBooleanFromStorage = (key: string): boolean => {
  const raw = localStorage.getItem(key);
  if (raw === null) return false;
  try {
    return JSON.parse(raw) === true;
  } catch {
    return false;
  }
};

const parseEnumFromStorage = <T extends string>(
  key: string,
  allowed: readonly T[],
  fallback: T,
): T => {
  const raw = localStorage.getItem(key);
  return raw !== null && (allowed as readonly string[]).includes(raw) ? (raw as T) : fallback;
};

export const getInitialInternalToolsUIState = () => ({
  isV5ThemeEnabled: parseBooleanFromStorage(V5_THEME_ENABLED_STORAGE_KEY),
  v5ColorMode: parseEnumFromStorage(V5_COLOR_MODE_STORAGE_KEY, V5_COLOR_MODES, DEFAULT_V5_COLOR_MODE),
  v5Density: parseEnumFromStorage(V5_DENSITY_STORAGE_KEY, V5_DENSITY_MODES, DEFAULT_V5_DENSITY),
});

export type IInternalToolsUIStore = Instance<typeof InternalToolsUI>;

export default InternalToolsUI;
